/**
 * Narrowing what the board shows, without changing what it fetched.
 *
 * ## Why these are not part of `BoardQuery`
 *
 * Client, Application, Project and Assignee decide **what is loaded**. They are
 * query parameters, they live in the URL, and a filtered board is a link
 * somebody can send.
 *
 * Search and the completed-stage filter decide **what you are looking at** in
 * data the board already holds. They cost no round trip, they apply as you
 * type, and they are deliberately not in the URL — the same distinction the
 * work tree's "Find" box already makes.
 *
 * If a searched board ever needs to be shareable, that is the moment to move
 * them into `BoardQuery`, not before.
 */
import type { Lane, Stage } from '@botguide/shared';

/** Which completed work to show. Only the Completed lane carries these stages. */
export type CompletedFilter = 'all' | 'pilot' | 'production';

export interface BoardView {
  /** Free text. Empty or blank means no filter, never "match nothing". */
  search: string;
  completed: CompletedFilter;
}

export const NO_VIEW: BoardView = { search: '', completed: 'all' };

/** Whether anything is narrowing the board right now. */
export function isNarrowed(view: BoardView): boolean {
  return view.search.trim() !== '' || view.completed !== 'all';
}

/**
 * Case-insensitive substring match on a task title.
 *
 * An empty or blank box keeps everything. That is not pedantry: the
 * alternative is a board that empties the moment somebody clicks the field and
 * types a space, which reads as broken rather than as filtered.
 */
export function matchesSearch(title: string, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  return title.toLowerCase().includes(needle);
}

/**
 * Whether one task survives the current view.
 *
 * The completed filter is checked **only against the completed lane**. Nothing
 * else carries a Pilot or Production stage, so applying it elsewhere would
 * empty three columns to answer a question that was only ever about one.
 */
export function keepsTask(
  lane: Lane,
  stage: Stage | null,
  task: { title: string },
  view: BoardView,
): boolean {
  if (!matchesSearch(task.title, view.search)) return false;
  if (lane !== 'completed' || view.completed === 'all') return true;
  return stage === view.completed;
}

/**
 * Narrow the tasks inside each group, keeping every group.
 *
 * A group that loses all its tasks stays, empty. The board's value is seeing
 * *where* the work is, and a column vanishing because a search excluded it
 * hides the very thing being asked about.
 */
export function narrowGroups<
  T extends { lane: Lane; stage: Stage | null; tasks: { title: string }[] },
>(groups: T[], view: BoardView): T[] {
  return groups.map((group) => ({
    ...group,
    tasks: group.tasks.filter((task) => keepsTask(group.lane, group.stage, task, view)),
  }));
}
